import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import FloatingWhatsApp from '../components/FloatingWhatsApp';
import MobileBottomNav from '../components/MobileBottomNav';
import SEOSchema from '../components/SEOSchema';
import ProgramFramework from '../components/ProgramFramework';
import ReviewSnippets from '../components/ReviewSnippets';
import { PROGRAM_FAQS } from '../data/programFaqs';
import { Scale, ArrowRight, Stethoscope, Activity } from 'lucide-react';

const WeightManagementPage = () => (
  <div className="min-h-screen bg-slate-950">
    <Helmet>
      <title>Doctor-Led Weight Management Program | Medical &amp; Sustainable | TCUBE FITNESS</title>
      <meta name="description" content="A doctor-led weight management program that considers your medical history, medications, labs, metabolism and lifestyle, not just calories. Personalized, sustainable and 100% online." />
      <meta name="keywords" content="doctor led weight management, medical weight loss, doctor supervised weight loss, sustainable weight loss, metabolic weight management, online weight loss program India, weight management doctor" />
      <link rel="canonical" href="https://tcubefitness.com/doctor-led-weight-management" />
    </Helmet>

    <SEOSchema
      type="webpage"
      name="Doctor-Led Weight Management Program"
      description="A medically informed, sustainable weight management program built around your health, not generic calorie targets."
      url="/doctor-led-weight-management"
      breadcrumbs={[
        { name: 'Home', path: '/' },
        { name: 'Programs', path: '/#programs' },
        { name: 'Doctor-Led Weight Management', path: '/doctor-led-weight-management' },
      ]}
      faq={(PROGRAM_FAQS['weight-management'] || []).map((f) => ({ question: f.q, answer: f.a }))}
    />

    <AnnouncementBanner />
    <Header />

    <main id="main-content" tabIndex={-1}>
      <section className="pt-32 sm:pt-36 pb-16 relative overflow-hidden">
        <div className="absolute top-10 right-1/4 w-80 h-80 bg-blue-600/10 rounded-full blur-3xl" aria-hidden="true"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center max-w-3xl">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
            <Scale className="w-4 h-4" aria-hidden="true" /> Doctor-Led Weight Management
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Doctor-Led <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Weight Management</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
            Your weight is more than calories and willpower. This program is led by a General Medicine (Internal Medicine) physician and considers your medical history, medications, lab markers, metabolism and lifestyle, to support sustainable, healthy weight management. Not another generic diet.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book-consultation?from=weight-management" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-7 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all shadow-lg shadow-blue-500/25" data-testid="weight-cta-book">
              Book Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/health-analysis" className="inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-7 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all" data-testid="weight-cta-analysis">
              Start Full Body Health Analysis
            </Link>
          </div>
        </div>
      </section>

      {/* Why it is different */}
      <section className="py-14 relative" aria-labelledby="weight-why-heading">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <h2 id="weight-why-heading" className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center">Why medical context matters for weight</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Stethoscope, t: 'Conditions & medications', d: 'Thyroid, PMOS, insulin resistance and certain medications can all affect weight. We account for them.' },
              { icon: Activity, t: 'Metabolic markers', d: 'We consider relevant markers and body composition, not just the number on the scale.' },
              { icon: Scale, t: 'Sustainable, not extreme', d: 'A moderate, consistent approach that protects muscle and fits your life, reviewed over time.' },
            ].map((c, i) => (
              <div key={i} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
                <c.icon className="w-6 h-6 text-blue-400 mb-3" aria-hidden="true" />
                <h3 className="text-white font-bold mb-2">{c.t}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReviewSnippets
        ids={["sustainable-approach", "sharvya-gut-fitness"]}
        heading="Real client experiences"
        subheading="Genuine stories from people we have supported. Individual results vary."
      />

      <ProgramFramework slug="weight-management" />
    </main>

    <Footer />
    <FloatingWhatsApp />
    <MobileBottomNav />
  </div>
);

export default WeightManagementPage;
